import { Component, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideCircleX } from '@ng-icons/lucide';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { UserStore } from '../../auth/store/user.store';
import { CartographieStore } from '../../cartographie/store/cartographie.store';

import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { Étape } from '../models/étape.model';
import { ÉTAT_VALIDATION_ÉTAPE } from '../models/état-validation-étape.enum';
import { EtapeStore } from '../parcours-etape/store/etape.store';
import { ParcoursStateService } from '../services/parcours-state.service';

@Component({
  selector: 'app-button-etape-close-panel',
  imports: [HlmButtonImports, HlmIconImports],
  providers: [provideIcons({ lucideCircleX, lucideCheck })],
  template: ` <div class="flex justify-between">
    <button
      class="text-components-content-neutral-secondary component-medium-medium"
      (click)="closeSidepanel()"
      hlmBtn
      variant="link"
    >
      Fermer le panneau
    </button>
    @if (!etape()?.estSupprimee && !etape()?.estExportee) {
      @if (this.userStore.isAdmin()) {
        <button
          class="w-full"
          class="text-components-content-neutral-secondary component-medium-medium"
          (click)="toggleValidation()"
          hlmBtn
          variant="secondary"
        >
          @if (etape()?.etatValidation === ETAT_VALIDATION_ETAPE.VALIDEE) {
            <ng-icon name="lucideX" hlm size="sm" />
            Dévalider l'étape
          } @else {
            <ng-icon name="lucideCheck" hlm size="sm" />
            Valider l'étape
          }
        </button>
      } @else if (this.userStore.canAskValidationEtape()) {
        <button
          class="w-full"
          class="text-components-content-neutral-secondary component-medium-medium"
          (click)="toggleDemandeValidation()"
          hlmBtn
          variant="secondary"
        >
          @if (etape()?.etatValidation === ETAT_VALIDATION_ETAPE.EN_COURS) {
            <ng-icon name="lucideCheck" hlm size="sm" />
            Demander la validation
          } @else {
            <ng-icon name="lucideX" hlm size="sm" />
            Annuler la validation
          }
        </button>
      }
    } @else if (!etape()?.estSupprimee && etape()?.estExportee && this.userStore.isAdmin()) {
      <button
        class="w-full"
        class="text-components-content-neutral-secondary component-medium-medium"
        (click)="deverrouillerEtape()"
        hlmBtn
        variant="secondary"
      >
        <ng-icon name="lucideX" hlm size="sm" />
        Déverrouiller l'étape
      </button>
    }
  </div>`,
})
export class ButtonEtapeClosePanelComponent {
  readonly #sidepanelService = inject(SidepanelService);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #referentielStore = inject(ReferentielStore);
  readonly userStore = inject(UserStore);
  readonly #parcoursStateService = inject(ParcoursStateService);
  readonly #estVerrouilleService = inject(EstVerrouilleService);
  readonly #etapeStore = inject(EtapeStore);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly etape = computed(() => this.#etapeStore.selectedEntity());
  readonly cartographie = computed(() => this.#cartographieStore.selectedEntity()!);
  readonly referentiel = computed(() => this.#referentielStore.selectedEntity());
  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(LivrablePrincipal.PARCOURS_CROISEMENT, this.cartographie(), this.referentiel()),
  );
  protected readonly ETAT_VALIDATION_ETAPE = ÉTAT_VALIDATION_ÉTAPE;

  closeSidepanel() {
    this.#sidepanelService.close();
  }

  toggleValidation() {
    const etape = this.etape();

    if (!etape) return;

    if (!this.handleValidationEtape(etape)) return;

    const toastrId = uuidv7();

    const { nom, codeEtape, duree, typesContrat, repartitionsAnnee, type, etatValidation } = etape;

    const étatValidationÉtape =
      etatValidation === ÉTAT_VALIDATION_ÉTAPE.VALIDEE ? ÉTAT_VALIDATION_ÉTAPE.EN_COURS : ÉTAT_VALIDATION_ÉTAPE.VALIDEE;
    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: nom!,
            codeEtape: codeEtape,
            duree: duree,
            typesContrat: typesContrat,
            idModalite: etape.modalite?.id,
            aChangementVille: etape.aChangementVille ?? false,
            aCursusInternational: etape.aCursusInternational ?? false,
            estCertifiante: etape.estCertifiante ?? false,
            idCertification: etape.idCertification,
            estOuverte: etape.estOuverte,
            etatValidation: étatValidationÉtape,
            repartitionsAnnee: repartitionsAnnee,
            type: type,
          },
        ],
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message:
              étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.VALIDEE
                ? "L'étape est bien marquée comme validée et elle est fermée à la modification."
                : "L'étape est invalidée. Vous pouvez maintenant la modifier.",
            titre: `Étape ${étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.VALIDEE ? 'validée' : 'invalidée'}`,
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }

  toggleDemandeValidation() {
    const etape = this.etape();

    if (!etape) return;

    if (!this.handleValidationEtape(etape)) return;

    const toastrId = uuidv7();
    const { nom, codeEtape, duree, typesContrat, repartitionsAnnee, type, etatValidation } = etape;

    const étatValidationÉtape =
      etatValidation === ÉTAT_VALIDATION_ÉTAPE.EN_COURS
        ? ÉTAT_VALIDATION_ÉTAPE.A_VALIDER
        : ÉTAT_VALIDATION_ÉTAPE.EN_COURS;
    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: nom!,
            codeEtape: codeEtape,
            duree: duree,
            typesContrat: typesContrat,
            idModalite: etape.modalite?.id,
            aChangementVille: etape.aChangementVille ?? false,
            aCursusInternational: etape.aCursusInternational ?? false,
            estCertifiante: etape.estCertifiante ?? false,
            idCertification: etape.idCertification,
            estOuverte: etape.estOuverte,
            repartitionsAnnee: repartitionsAnnee,
            type: type,
            etatValidation: étatValidationÉtape,
          },
        ],
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message:
              étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.A_VALIDER
                ? "L'étape est en attente de validation par un administrateur et fermée à la modification."
                : 'Vous pouvez maintenant la modifier.',
            titre: `La demande de validation est ${étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.A_VALIDER ? 'faite' : 'annulée'}`,
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }

  handleValidationEtape(etape: Étape) {
    const {
      nom,
      codeEtape,
      duree,
      typesContrat,
      repartitionsAnnee,
      type,
      estDupliquee,
      certification,
      etatValidation,
    } = etape;

    const toastrId = uuidv7();

    if (
      (!nom ||
        !codeEtape ||
        !duree ||
        !typesContrat?.length ||
        (!type && !etape.estCertifiante) ||
        (repartitionsAnnee ?? []).filter((x) => x.nombreMois === 0).length > 0 ||
        (typesContrat.includes('apprentissage') && !certification) ||
        !this.isCodeEtapeValid(codeEtape)) &&
      !estDupliquee &&
      etatValidation !== ÉTAT_VALIDATION_ÉTAPE.VALIDEE
    ) {
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message:
            "Pour valider l'étape, vous devez renseigner les champs marqués comme requis dans les informations de l'étape.",
          id: toastrId,
        },
        id: toastrId,
      });

      this.navigateWithPathParameters(etape.id, true, true);
      return false;
    }

    return true;
  }

  navigateWithPathParameters(id: string, detail: boolean, showErrors: boolean) {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        id: id,
        niveau: this.#parcoursStateService.profondeurActuelle(),
        type: 'étape',
        detail: detail ?? this.#sidepanelService.isOpen(),
        showErrors: showErrors,
      },
      queryParamsHandling: 'merge',
    });
  }

  isCodeEtapeValid(codeEtape?: string) {
    return codeEtape?.match('[A-Z]{3}(0|[3-8])[A-Z]{3}\\d+\\.\\d{1,2}|[A-Z]{3}(0|[3-8])[A-Z]{3}\\d{3}');
  }

  deverrouillerEtape() {
    const etape = this.etape();

    if (!etape) return;

    const toastrId = uuidv7();

    const { nom, codeEtape, duree, typesContrat, repartitionsAnnee, type, estExportee } = etape;

    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: nom!,
            codeEtape: codeEtape,
            duree: duree,
            typesContrat: typesContrat,
            idModalite: etape.modalite?.id,
            aChangementVille: etape.aChangementVille ?? false,
            aCursusInternational: etape.aCursusInternational ?? false,
            estCertifiante: etape.estCertifiante ?? false,
            idCertification: etape.idCertification,
            estOuverte: etape.estOuverte,
            etatValidation: etape.etatValidation,
            repartitionsAnnee: repartitionsAnnee,
            type: type,
            estExportee: estExportee ? false : true,
          },
        ],
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: "L'étape est ouverte à la modification.",
            titre: `Étape déverrouillée`,
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }
}
